import { LuMapPin } from 'react-icons/lu';

import type { SensitiveSiteLimit } from '~api/services';
import { Polygon, SvgTooltip } from '../components';
import { useLayerTooltip } from '../hooks';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type SensitiveSitesLayerProps = {
  /** Optional sensitive sites to display */
  sensitiveSites?: SensitiveSiteLimit[];
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const getSensitiveSiteId = (site: SensitiveSiteLimit) => site.site_name;
const TOOLTIP_CONTENT: { label: string; value: (site: SensitiveSiteLimit | null) => string }[] = [
  {
    label: 'Site Type',
    value: (site) => site?.arcgis_response?.site_type ?? 'N/A',
  },
  {
    label: 'Distance',
    value: (site) => site?.blast_design_constraints?.distance_html ?? 'N/A',
  },
  {
    label: 'Vibration Limit',
    value: (site) => site?.blast_design_constraints?.vibration_limit_html ?? 'N/A',
  },
  {
    label: 'Flyrock SDoB',
    value: (site) => site?.blast_design_constraints?.flyrock_limit_html ?? 'N/A',
  },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Layer to render sensitive sites as polygons on the blast optimiser canvas.
 *
 * @example
 * ```tsx
 * <SensitiveSitesLayer sensitiveSites={sensitiveSitesData} />
 * ```
 */
const SensitiveSitesLayer = ({ sensitiveSites }: SensitiveSitesLayerProps) => {
  const { layerRef, tooltipRef, hoveredItem } = useLayerTooltip({
    items: sensitiveSites,
    getId: getSensitiveSiteId,
  });

  if (!sensitiveSites || sensitiveSites.length === 0) return null;

  return (
    <>
      <g className="sensitive-sites" ref={layerRef} aria-label="Sensitive sites layer">
        {sensitiveSites.map((site) => {
          const geom = site.arcgis_response.geometry;
          const polygons =
            geom.type === 'MultiPolygon'
              ? geom.coordinates.map((coords) => ({ type: 'Polygon' as const, coordinates: coords }))
              : [geom];

          return (
            <g
              key={site.site_name}
              className="sensitive-site"
              data-item-id={site.site_name}
              aria-label={`Sensitive site: ${site.site_name}`}
            >
              {polygons.map((polygon, i) => (
                <Polygon
                  // biome-ignore lint/suspicious/noArrayIndexKey: Stable sub-polygon order within a site
                  key={i}
                  className="sensitive-site-polygon transition-[fill-opacity] duration-200 ease-in-out hover:cursor-pointer hover:[fill-opacity:0.5]"
                  geometry={polygon}
                  fill={
                    site.blast_design_constraints?.display_hints?.fields?.site_name?.colour ?? 'var(--color-pink-400)'
                  }
                  fillOpacity={0.15}
                  stroke="var(--color-pink-500)"
                />
              ))}
            </g>
          );
        })}
      </g>
      {/* Tooltip */}
      <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
        {hoveredItem && (
          <div className="flex flex-col">
            <h4 className="flex items-center gap-1 rounded-t-md bg-muted px-3 py-2 font-medium text-base">
              <LuMapPin size={16} />
              {hoveredItem.site_name}
            </h4>
            <div className="flex flex-col gap-2 rounded-b-md px-3 py-2">
              {TOOLTIP_CONTENT.map(({ label, value }) => (
                <div key={label} className="flex items-center gap-1 text-sm">
                  <span className="font-bold">{label}:</span>
                  {/** biome-ignore lint/style/useNamingConvention: That's how `dangerouslySetInnerHTML` works with JSX  */}
                  {/** biome-ignore lint/security/noDangerouslySetInnerHtml: This is safe because the content is controlled and sanitized in the backend */}
                  <span dangerouslySetInnerHTML={{ __html: value(hoveredItem) }}></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </SvgTooltip>
    </>
  );
};

export { SensitiveSitesLayer };
